import React, { useEffect, useState } from 'react';
import { RefreshCw, Quote } from 'lucide-react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { useQuestion } from '../hooks/useQuestion';
import { settingsService } from '../services/settings';
import { cn } from '../utils/cn';
import { PinInput } from './PinInput';

export function QuestionDisplay() {
  const { question, isRefreshing, fetchRandomQuestion } = useQuestion();
  const [settings, setSettings] = React.useState({ logoUrl: null, isProtected: false });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [pinError, setPinError] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const currentSettings = await settingsService.get();
        setSettings(currentSettings);
        setIsLocked(currentSettings.isProtected || false);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  const handleNewQuestion = async () => {
    setIsAnimating(true);
    await fetchRandomQuestion();
    setTimeout(() => setIsAnimating(false), 150);
  };

  const handlePinSubmit = async (pin: string) => {
    try {
      setPinError(null);
      const isValid = await settingsService.verifyPin(pin);
      if (isValid) {
        setIsLocked(false);
      } else {
        setPinError('Code incorrect');
      }
    } catch (error) {
      setPinError('Erreur lors de la vérification du code');
    }
  };

  if (settings.isProtected && isLocked) {
    return <PinInput onSubmit={handlePinSubmit} error={pinError} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <Card className="max-w-3xl w-full bg-white/95 backdrop-blur-lg">
        <div className="text-center">
          {settings.logoUrl ? (
            <div className="flex items-center justify-center mb-8 md:mb-12">
              <img 
                src={settings.logoUrl} 
                alt="Logo" 
                className="h-16 md:h-20 object-contain"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center mb-8 md:mb-12">
              <Quote className="w-12 h-12 md:w-16 md:h-16 text-indigo-600" strokeWidth={1.5} />
            </div>
          )}
          
          <div className="min-h-[120px] md:min-h-[160px] flex items-center justify-center mb-8 md:mb-12 px-4 md:px-8 relative">
            <div 
              className={cn(
                "w-full transition-all duration-150 transform",
                isAnimating && "opacity-0 translate-y-2",
                !isAnimating && "opacity-100 translate-y-0"
              )}
            >
              {isRefreshing ? (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                question ? (
                  <p className="text-2xl md:text-3xl text-gray-800 leading-relaxed font-light px-2 md:px-8">
                    {question.content}
                  </p>
                ) : (
                  <p className="text-xl md:text-2xl text-gray-500 font-light">
                    Aucune question disponible
                  </p>
                )
              )}
            </div>
          </div>

          <Button
            onClick={handleNewQuestion}
            isLoading={isRefreshing}
            icon={<RefreshCw className={cn(
              "w-5 h-5",
              isRefreshing && "animate-spin"
            )} />}
            className={cn(
              "transition-all duration-150 active:scale-95 hover:shadow-lg px-6 md:px-8 py-2.5 md:py-3 text-base md:text-lg",
              isRefreshing && "opacity-50 cursor-not-allowed"
            )}
            disabled={isRefreshing}
          >
            Nouvelle Question
          </Button>
        </div>
      </Card>
    </div>
  );
}